import AdminOptionsIcon from "../components/icons/AdminOptionsIcon";
import AppSettingsSidebarIcon from "../components/icons/AppSettingsSidebarIcon";
import CustomerSupportSidebarIcon from "../components/icons/CustomerSupportSidebarIcon";
import DeveloperIcon from "../components/icons/DeveloperIcon";
import EmployeesIcon from "../components/icons/EmployeesIcon";
import LogsIcon from "../components/icons/LogsIcon";
import RegionSidebarIcon from "../components/icons/RegionSidebarIcon";
import SocialMediaSidebarIcon from "../components/icons/SocialMediaSidebarIcon";
import SquadsSidebarIcon from "../components/icons/SquadsSidebarIcon";
import SupportTicketsSidebarIcon from "../components/icons/SupportTicketsSidebarIcon";
import TimelinesSidebarIcon from "../components/icons/TimelinesSidebarIcon";
import TournamentCommentsSidebarIcon from "../components/icons/TournamentCommentsSidebarIcon";
import TournamentSidebarIcon from "../components/icons/TournamentSidebarIcon";
import UsersSidebarIcon from "../components/icons/UsersSidebarIcon";
import { ROLES } from "./roleRules";

// What each permission controls in the dashboard, laid out like the sidebar:
// section → its pages (tabs) → the buttons inside them. `permission` is the
// check that shows that node. `alsoNeeds` lists what the backend additionally
// requires for it to work. `hint` is an explanation shown behind a "?" on the
// roles page.
//
// A section has no permission of its own: every tab has its own permission,
// the section shows when the admin holds any of them, and each tab works with
// its own permission alone. The sidebar, the section's tab bar, its routes and
// the roles page all read this file.

export const PERMISSION_LABELS = {
  "view-statistics": "الإحصائيات",
  "control-app-admins": "إدارة أدمن التطبيق",
  "control-ads": "التحكم في الإعلانات",
  "view-logs": "سجل العمليات",
  "view-room-efficiency": "كفاءة الغرف",
  "view-admins": "عرض الموظفين",
  "create-admins": "إضافة موظف",
  "update-admins": "تعديل موظف",
  "delete-admins": "حذف موظف",
  "control-hierarchy": "تسلسل الموظفين",
  "control-roles": "الأدوار والصلاحيات",
  "view-tournaments": "عرض البطولات",
  "create-tournaments": "إضافة بطولة",
  "update-tournaments": "تعديل بطولة",
  "delete-tournaments": "حذف بطولة",
  "control-tournament-templates": "قوالب تفاصيل البطولة",
  "control-tournament-images": "صور البطولات",
  "control-tournament-access": "الوصول إلى البطولات",
  "send-tournament-notification": "إشعار بطولة",
  "view-tables": "جداول البطولة",
  "create-tables": "إنشاء جداول",
  "update-tables": "إدارة اللاعبين في الجداول",
  "check-in-entries": "تأكيد حضور اللاعبين",
  "setup-next-stage": "تجهيز المرحلة المتقدمة",
  "manage-room-info": "معلومات وموعد الغرفة",
  "delete-tables": "حذف جدول",
  "send-table-notification": "إشعار الجدول",
  "view-winner-tables": "جداول الفائزين والمركز الثاني والثالث",
  "send-all-notification": "إشعار لكل اللاعبين",
  "send-user-notification": "إشعار للاعب",
  "view-timelines": "الجداول الزمنية",
  "assign-executors": "تعيين المنفذين",
  "control-region-images": "صور المناطق",
  "control-user-images": "صور اللاعبين",
  "control-squad-images": "صور السكوادات",
  "view-users": "عرض اللاعبين",
  "ban-users": "حظر اللاعبين",
  "delete-users": "حذف اللاعبين",
  "delete-game-ids": "حذف حسابات اللعبة",
  "view-squads": "عرض السكوادات",
  "ban-squads": "حظر السكوادات",
  "delete-squads": "حذف السكوادات",
  "send-squad-notification": "إشعار للسكواد",
  "view-regions": "عرض المناطق",
  "create-regions": "إضافة منطقة",
  "update-regions": "تعديل منطقة",
  "delete-regions": "حذف منطقة",
  "view-user-chats": "شات المستخدمين",
  "view-prime-chats": "شات البرايم",
  "reply-user-chats": "الرد على اللاعبين",
  "control-chat-access": "إيقاف وتفعيل الشات",
  "view-prize-delivery": "تسليم الجوائز",
  "mark-prizes-delivered": "تأكيد تسليم الجوائز",
  "view-support-tickets": "تذاكر الدعم",
  "reply-support-tickets": "الرد على التذاكر",
  "change-ticket-status": "إغلاق وإعادة فتح التذاكر",
  "view-email-log": "سجل البريد الإلكتروني",
  "unsuppress-emails": "إزالة حظر عناوين البريد",
  "view-comment-reports": "ابلاغات التعليقات",
  "view-previous-reports": "الابلاغات السابقة",
  "view-all-comments": "جميع التعليقات",
  "hide-comments": "إخفاء وإظهار التعليقات",
  "delete-comments": "حذف التعليقات",
  "dismiss-comment-reports": "رفض الإبلاغات",
  "restore-comments": "استعادة التعليقات",
  "delete-user-comments": "حذف جميع تعليقات لاعب",
  "ban-commenters": "حظر من التعليق",
  "view-social-media": "روابط السوشال ميديا",
  "update-social-media": "تعديل روابط السوشال ميديا",
  "view-app-version": "إصدار التطبيق",
  "update-app-version": "تعديل إصدار التطبيق",
  "view-ad-analytics": "تحليلات الإعلانات",
};

export const PERMISSION_SECTIONS = [
  {
    id: "admin-options",
    label: "خيارات الادمن",
    icon: AdminOptionsIcon,
    pages: [
      { id: "statistics", path: "statistics", label: "الإحصائيات", permission: "view-statistics" },
      { id: "app-admins", path: "app-admins", label: "إدارة أدمن التطبيق", permission: "control-app-admins" },
      { id: "control-ads", path: "control-ads", label: "التحكم في الإعلانات", permission: "control-ads" },
      {
        id: "roles",
        path: "roles-permissions",
        label: "الأدوار والصلاحيات",
        permission: "control-roles",
        hint: "التعديل للأدمن والمالك فقط؛ غيرهم يرى الأدوار وموظفيها دون تعديل.",
      },
    ],
  },
  {
    id: "logs",
    label: "سجل العمليات",
    icon: LogsIcon,
    pages: [
      {
        id: "action-logs",
        path: "show-admin-action-logs",
        label: "سجل العمليات",
        permission: "view-logs",
        hint: "يعرض فقط عمليات الموظفين الذين يمكنه الوصول إليهم: هو ومن تحته في التسلسل. الأدمن والمالك يرون كل العمليات.",
      },
      {
        id: "room-efficiency",
        path: "room-information-efficiency",
        label: "كفاءة الغرف",
        permission: "view-room-efficiency",
        hint: "يعرض فقط البطولات التي يمكنه الوصول إليها. الأدمن والمالك يرون كل البطولات.",
      },
    ],
  },
  {
    id: "employees",
    label: "ادارة الموظفين",
    icon: EmployeesIcon,
    pages: [
      {
        id: "all-employees",
        path: "show-all-employees",
        label: "عرض جميع الموظفين",
        permission: "view-admins",
        actions: [
          {
            id: "edit-employee",
            label: "تعديل الموظف",
            permission: "update-admins",
            hint: "يعدّل فقط الموظفين التابعين له في التسلسل، ويعطيهم فقط الأدوار المسموح له بتعيينها. حساب المالك يعدّله المالك نفسه فقط.",
          },
          {
            id: "delete-employee",
            label: "حذف الموظف",
            permission: "delete-admins",
            hint: "يحذف فقط الموظفين التابعين له في التسلسل. حساب المالك لا يُحذف أبداً.",
          },
        ],
      },
      {
        id: "add-employee",
        path: "add-new-employee",
        label: "اضافة موظف جديد",
        permission: "create-admins",
        hint: "ينشئ موظفين بالأدوار المسموح له بتعيينها فقط (يمكنه تعيين). دور المالك يعطيه المالك فقط.",
      },
      {
        id: "hierarchy",
        path: "control-hierarchy",
        label: "التحكم في تسلسل الموظفين",
        permission: "control-hierarchy",
        hint: "يرى ويعدّل تسلسله ومن تحته فقط. الأدمن والمالك يرون كل التسلسل.",
      },
    ],
  },
  {
    id: "tournaments",
    label: "ادارة البطولات",
    icon: TournamentSidebarIcon,
    pages: [
      {
        id: "all-tournaments",
        path: "show-all-tournaments",
        label: "عرض جميع البطولات",
        permission: "view-tournaments",
        hint: "يعرض فقط البطولات التي يمكنه الوصول إليها. الأدمن والمالك يرون كل البطولات.",
        actions: [
          {
            id: "edit-tournament",
            label: "تعديل البطولة",
            permission: "update-tournaments",
            hint: "زر التعديل على بطاقة البطولة وصفحة التعديل، للبطولات التي يمكنه الوصول إليها فقط.",
          },
          {
            id: "delete-tournament",
            label: "حذف البطولة",
            permission: "delete-tournaments",
            hint: "يحذف فقط البطولات التي يمكنه الوصول إليها.",
          },
          {
            id: "tournament-notification",
            label: "إرسال إشعار للبطولة",
            permission: "send-tournament-notification",
            hint: "يرسل فقط للبطولات التي يمكنه الوصول إليها.",
          },
          {
            id: "tournament-tables",
            label: "جداول البطولة",
            permission: "view-tables",
            hint: "زر جداول البطولة في بطاقتها يفتح جداولها. يرى فقط جداول البطولات التي يمكنه الوصول إليها، والمنفذ (Co-Leader) يرى جداول مجموعاته فقط.",
            actions: [
              {
                id: "winner-tables",
                label: "جداول الفائزين والمركز الثاني والثالث",
                permission: "view-winner-tables",
                hint: "بدونها لا تظهر هذه الجداول ولا يمكن العمل عليها. منفذ النهائيات (Finals-Executer) يبقى على جداول المرحلة النهائية فقط.",
              },
              {
                id: "create-tables",
                label: "إنشاء جدول (تصفيات، نهائيات، مرحلة تالية)",
                permission: "create-tables",
                hint: "في البطولات التي يمكنه الوصول إليها فقط.",
              },
              {
                id: "setup-next-stage",
                label: "تجهيز المرحلة المتقدمة",
                permission: "setup-next-stage",
                hint: "ينشئ جداول المرحلة المتقدمة دفعة واحدة، فقط في البطولات التي يديرها كاملة.",
              },
              {
                id: "manage-table-players",
                label: "إدارة اللاعبين في الجداول",
                permission: "update-tables",
                hint: "إضافة اللاعبين ونقلهم وحذفهم ونقلهم للمرحلة التالية أو النهائي، في الجداول التي يصل إليها فقط.",
              },
              {
                id: "check-in-table-entries",
                label: "تأكيد حضور اللاعبين",
                permission: "check-in-entries",
                hint: "علامة الحضور بجانب كل لاعب أو فريق، في الجداول التي يصل إليها فقط.",
              },
              {
                id: "delete-table",
                label: "حذف جدول",
                permission: "delete-tables",
                hint: "الجداول التي يصل إليها فقط.",
              },
              {
                id: "room-information",
                label: "معلومات وموعد الغرفة",
                permission: "manage-room-info",
                hint: "الجداول التي يصل إليها فقط.",
              },
              {
                id: "table-notification",
                label: "إرسال إشعار للجدول",
                permission: "send-table-notification",
                hint: "يرسل فقط للجداول التي يصل إليها.",
              },
              {
                id: "tables-executors",
                label: "تعيين المنفذين",
                permission: "assign-executors",
                hint: "يسند المجموعات إلى المنفذين التابعين له، في البطولات التي يمكنه الوصول إليها فقط.",
              },
            ],
          },
        ],
      },
      { id: "add-tournament", path: "add-new-tournament", label: "اضافة بطولة جديده", permission: "create-tournaments" },
      {
        id: "details-templates",
        path: "tournament-details-templates",
        label: "قوالب تفاصيل البطولة",
        permission: "control-tournament-templates",
        hint: "إنشاء وتعديل وحذف القوالب. من يضيف أو يعدّل البطولات يرى القوالب للاختيار منها فقط.",
      },
      {
        id: "tournament-access",
        path: "control-tournaments-access",
        label: "التحكم في الوصول الى البطولات",
        permission: "control-tournament-access",
        hint: "يعطي الموظفين التابعين له الوصول إلى البطولات التي يصل إليها هو فقط.",
      },
      {
        id: "tournament-images",
        path: "edit-tournaments-images",
        label: "التحكم في صور البطولات",
        permission: "control-tournament-images",
        hint: "رفع وحذف صور البطولات. من يضيف أو يعدّل البطولات يرى الصور للاختيار منها فقط.",
      },
    ],
  },
  {
    id: "regions",
    label: "ادارة المناطق",
    icon: RegionSidebarIcon,
    pages: [
      {
        id: "all-regions",
        path: "show-all-regions",
        label: "عرض جميع المناطق",
        permission: "view-regions",
        actions: [
          { id: "edit-region", label: "تعديل المنطقة", permission: "update-regions" },
          { id: "delete-region", label: "حذف المنطقة", permission: "delete-regions" },
        ],
      },
      { id: "add-region", path: "add-new-region", label: "اضافة منطقة جديدة", permission: "create-regions" },
      {
        id: "region-images",
        path: "edit-regions-images",
        label: "التحكم في صور المناطق",
        permission: "control-region-images",
        hint: "رفع وحذف صور المناطق. من يضيف أو يعدّل المناطق يرى الصور للاختيار منها فقط.",
      },
    ],
  },
  {
    id: "users",
    label: "ادارة اللاعبين",
    icon: UsersSidebarIcon,
    pages: [
      {
        id: "all-users",
        path: "show-all-users",
        label: "عرض جميع اللاعبين",
        permission: "view-users",
        actions: [
          { id: "ban-user", label: "حظر وإلغاء حظر اللاعب", permission: "ban-users" },
          {
            id: "delete-user",
            label: "حذف اللاعب",
            permission: "delete-users",
            hint: "يحذف حساب اللاعب نهائياً.",
          },
          {
            id: "delete-game-id",
            label: "حذف حساب اللعبة",
            permission: "delete-game-ids",
            hint: "يزيل معرف اللعبة (الاسم والايدي) من حساب اللاعب، فيصبح متاحاً لغيره.",
          },
          { id: "notify-user", label: "إرسال إشعار للاعب", permission: "send-user-notification" },
          {
            id: "notify-all",
            label: "إرسال إشعار لكل اللاعبين",
            permission: "send-all-notification",
            hint: "يصل لكل لاعبي التطبيق.",
          },
        ],
      },
      { id: "user-images", path: "edit-users-images", label: "التحكم في صور اللاعبين", permission: "control-user-images" },
    ],
  },
  {
    id: "squads",
    label: "ادارة السكوادات",
    icon: SquadsSidebarIcon,
    pages: [
      {
        id: "all-squads",
        path: "show-all-squads",
        label: "عرض جميع السكوادات",
        permission: "view-squads",
        actions: [
          { id: "ban-squad", label: "حظر وإلغاء حظر السكواد", permission: "ban-squads" },
          {
            id: "squad-notification",
            label: "إرسال إشعار للسكواد",
            permission: "send-squad-notification",
            hint: "يصل الإشعار لكل الأعضاء الحاليين في السكواد، ومنهم صاحب السكواد.",
          },
          {
            id: "delete-squad",
            label: "حذف السكواد",
            permission: "delete-squads",
            hint: "يحذف السكواد نهائياً.",
          },
        ],
      },
      { id: "squad-images", path: "control-squad-images", label: "صور السكوادات", permission: "control-squad-images" },
    ],
  },
  {
    id: "timelines",
    label: "الجداول الزمنية",
    icon: TimelinesSidebarIcon,
    pages: [
      {
        id: "timelines",
        path: "show-all-timelines",
        label: "عرض جميع الجداول الزمنية",
        permission: "view-timelines",
        hint: "يعرض فقط البطولات التي يمكنه الوصول إليها. المنفذ (Co-Leader) يرى فقط المجموعات المسندة له.",
        actions: [
          {
            id: "timeline-executors",
            label: "تعيين المنفذين",
            permission: "assign-executors",
            hint: "يسند المجموعات إلى المنفذين التابعين له، في البطولات التي يمكنه الوصول إليها فقط.",
          },
        ],
      },
    ],
  },
  {
    id: "customer-support",
    label: "شات المستخدمين",
    icon: CustomerSupportSidebarIcon,
    pages: [
      {
        id: "users-chat",
        path: "show-users-chats",
        label: "شات المستخدمين",
        permission: "view-user-chats",
        hint: "قراءة كل محادثات اللاعبين مع الدعم.",
        actions: [
          {
            id: "reply-user-chat",
            label: "الرد على اللاعبين",
            permission: "reply-user-chats",
            hint: "إرسال الرسائل، وتعديل وحذف رسائل الدعم فقط — لا يعدّل رسائل اللاعبين.",
          },
          {
            id: "chat-access",
            label: "إيقاف وتفعيل الشات للاعب",
            permission: "control-chat-access",
          },
        ],
      },
      {
        id: "prime-chats",
        path: "show-prime-chats",
        label: "شات البرايم",
        permission: "view-prime-chats",
        hint: "محادثات لاعبي البرايم فقط. الرد وإيقاف الشات يتبعان صلاحيات شات المستخدمين.",
      },
      {
        id: "prize-delivery",
        path: "show-prize-Delivery",
        label: "تسليم الجوائز",
        permission: "view-prize-delivery",
        actions: [
          { id: "mark-delivered", label: "تأكيد تسليم الجائزة", permission: "mark-prizes-delivered" },
        ],
      },
    ],
  },
  {
    id: "support-tickets",
    label: "تذاكر الدعم",
    icon: SupportTicketsSidebarIcon,
    pages: [
      {
        id: "tickets",
        path: "tickets",
        label: "التذاكر",
        permission: "view-support-tickets",
        hint: "قراءة كل تذاكر اللاعبين ورسائلها.",
        actions: [
          {
            id: "reply-ticket",
            label: "الرد على التذاكر",
            permission: "reply-support-tickets",
            hint: "إرسال الردود مع الصور. ردّ الدعم يُشعر اللاعب ويعيد التذكرة لانتظاره.",
          },
          { id: "ticket-status", label: "إغلاق وإعادة فتح التذكرة", permission: "change-ticket-status" },
        ],
      },
      {
        id: "email-delivery",
        path: "email-delivery",
        label: "سجل البريد الإلكتروني",
        permission: "view-email-log",
        hint: "البحث عن رسائل البريد المرسلة لعنوان خلال آخر 30 يوماً.",
        actions: [
          {
            id: "unsuppress-email",
            label: "إزالة حظر عنوان بريد",
            permission: "unsuppress-emails",
            hint: "يسمح بالإرسال مجدداً لعنوان توقف الإرسال إليه بسبب ارتداد أو شكوى.",
          },
        ],
      },
    ],
  },
  {
    id: "tournament-comments",
    label: "تعليقات البطولات",
    icon: TournamentCommentsSidebarIcon,
    pages: [
      {
        id: "comment-reports",
        path: "show-comments-reports",
        label: "ابلاغات التعليقات",
        permission: "view-comment-reports",
        actions: [
          { id: "reports-hide", label: "إخفاء وإظهار التعليق", permission: "hide-comments" },
          { id: "reports-delete", label: "حذف التعليق", permission: "delete-comments" },
          {
            id: "reports-dismiss",
            label: "رفض الإبلاغات",
            permission: "dismiss-comment-reports",
            hint: "يغلق الإبلاغات ويبقي التعليق كما هو.",
          },
        ],
      },
      {
        id: "previous-reports",
        path: "previous-reports",
        label: "الابلاغات السابقة",
        permission: "view-previous-reports",
        actions: [
          { id: "previous-hide", label: "إخفاء وإظهار التعليق", permission: "hide-comments" },
          { id: "previous-delete", label: "حذف التعليق", permission: "delete-comments" },
          { id: "previous-restore", label: "استعادة تعليق محذوف", permission: "restore-comments" },
        ],
      },
      {
        id: "all-comments",
        path: "all-comments",
        label: "جميع التعليقات",
        permission: "view-all-comments",
        actions: [
          { id: "all-delete", label: "حذف التعليق", permission: "delete-comments" },
          {
            id: "all-delete-user",
            label: "حذف جميع تعليقات المستخدم",
            permission: "delete-user-comments",
            hint: "يحذف كل تعليقات هذا اللاعب في البطولة دفعة واحدة.",
          },
          {
            id: "all-ban",
            label: "حظر المستخدم من التعليق",
            permission: "ban-commenters",
            hint: "يمنع اللاعب من كتابة التعليقات.",
          },
        ],
      },
    ],
  },
  {
    id: "social-media",
    label: "السوشال ميديا",
    icon: SocialMediaSidebarIcon,
    pages: [
      {
        id: "social-links",
        path: "show-social-media",
        label: "روابط السوشال ميديا",
        permission: "view-social-media",
        actions: [
          {
            id: "update-social-links",
            label: "تعديل الروابط",
            permission: "update-social-media",
            hint: "الروابط تظهر لكل اللاعبين في الصفحة الرئيسية للتطبيق.",
          },
        ],
      },
    ],
  },
  {
    id: "app-settings",
    label: "التطبيق",
    icon: AppSettingsSidebarIcon,
    pages: [
      {
        id: "app-version",
        path: "app-version",
        label: "إصدار التطبيق",
        permission: "view-app-version",
        actions: [
          {
            id: "update-app-version",
            label: "تعديل الإصدار",
            permission: "update-app-version",
            hint: "يحدد أحدث إصدار وأقل إصدار مدعوم والتحديث الإجباري لكل اللاعبين على المنصة.",
          },
        ],
      },
    ],
  },
  {
    id: "developer",
    label: "أدوات المطور",
    icon: DeveloperIcon,
    pages: [
      {
        id: "ad-analytics",
        path: "ad-analytics",
        label: "تحليلات الإعلانات",
        permission: "view-ad-analytics",
      },
    ],
  },
];

export const sectionOf = (id) => PERMISSION_SECTIONS.find((section) => section.id === id);

// The permissions that show a section: any of its tabs'.
export const sectionPermissions = (section) => section.pages.map((page) => page.permission);

export const hasAnyPermission = (held, permissions) =>
  permissions.some((permission) => held.includes(permission));

export const canOpenSection = (held, sectionId) =>
  hasAnyPermission(held, sectionPermissions(sectionOf(sectionId)));

// The tabs of a section the admin may open, in order.
export const allowedTabs = (sectionId, held) =>
  sectionOf(sectionId).pages.filter((page) => held.includes(page.permission));

// Every place a permission shows up, as "section › page › button".
export const PERMISSION_PLACES = (() => {
  const places = {};
  const add = (key, path) => {
    if (!key) return;
    (places[key] ||= []).push(path);
  };
  for (const section of PERMISSION_SECTIONS) {
    for (const page of section.pages) {
      add(page.permission, `${section.label} › ${page.label}`);
      const walk = (actions, path) => {
        for (const action of actions || []) {
          const actionPath = `${path} › ${action.label}`;
          add(action.permission, actionPath);
          walk(action.actions, actionPath);
        }
      };
      walk(page.actions, `${section.label} › ${page.label}`);
    }
  }
  return places;
})();

// Every permission the dashboard knows: the labelled ones and every one placed
// on a page or button. A role holding anything else still has values from
// before the permission migration (the roles page won't save it). Counting
// the placed ones too means a missing label shows the key instead of
// blocking saves.
export const KNOWN_PERMISSIONS = [
  ...new Set([...Object.keys(PERMISSION_LABELS), ...Object.keys(PERMISSION_PLACES)]),
];

// Turning a permission off also turns off the permissions that only live
// underneath it (e.g. a section's buttons), so a role never keeps access the
// dashboard can no longer show.
export const PERMISSION_DEPENDENTS = (() => {
  const dependents = {};
  for (const [key, roots] of Object.entries(PERMISSION_PLACES)) {
    dependents[key] = Object.entries(PERMISSION_PLACES)
      .filter(
        ([other, places]) =>
          other !== key &&
          places.every((place) => roots.some((root) => place.startsWith(`${root} › `))),
      )
      .map(([other]) => other);
  }
  return dependents;
})();

// Built-in roles in the order the roles page lists them; others follow by name.
export const ROLE_DISPLAY_ORDER = [
  ROLES.OWNER,
  ROLES.ADMIN,
  ROLES.TOP_LEADER,
  ROLES.LEADER,
  ROLES.CO_LEADER,
  ROLES.FINALS_EXECUTER,
  ROLES.CUSTOMER_SERVICE,
  ROLES.OBSERVER,
  ROLES.CHAT_ADMIN,
];

// Arabic title shown as a badge on a role's card.
export const ROLE_BADGES = {
  [ROLES.OWNER]: "المالك",
};

// Subtitle under a role's name; roles without one show none.
export const ROLE_DESCRIPTIONS = {
  [ROLES.OWNER]: "مالك التطبيق",
};
